import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useState } from 'react';

import { ExceptionalPartForm, ExceptionalPartData } from './ExceptionalPartForm';
import { PartActionSearchField } from './PartActionSearchField';
import {
  Action,
  PartActionType,
  CreateBulkPartActionsRequest,
} from '../../../../../@types/partAction.type';
import {
  PartTypeActionLiveSearchItem,
  PartTypeActionLiveSearchItemCategoryCode,
} from '../../../../../@types/partType.type';
import { PartTypeLocationItemData } from '../../../../../components/molecules/PartTypeLocationItem';
import { PartLocationSelectPopup } from '../../../../../components/organisms/PartLocationSelectPopup';
import { ServiceTypeCode } from '../../../../../configs/enums';
import { CONSUMABLE_MAX_QTY, clampConsumableQty } from '../../../../../constants/partAction';
import { useGetPartLocationDataByServiceId } from '../../../../../queries/part-query';
import { partActionInValidations } from '../../../../../utils/part-action';
import { PartActionSubmitInput } from '../../useServiceDialogs';

export interface PartActionDialogProps {
  open: boolean;
  onClose: () => void;
  serviceId: number;
  clientId: number;
  contractId: number;
  locationId: number;
  serviceTypeCode: string;
  /**
   * Reported-serial part id from the parent service — drives the default
   * suggestions list for Test/Repair/Adjust/Clean/DOA so users see relevant
   * parts (installed-in-job, reported model, repaired part) without typing.
   */
  reportedSerialNumberPartId?: number;
  canCreateExceptional: boolean;
  /**
   * Commits the part action. Resolves `true` once it has actually committed so
   * the dialog can clear itself for the next action; a failed or
   * interaction-form-gated submit resolves falsy, leaving the entered details in
   * place. (A plain `void` return also leaves them, e.g. in tests.)
   */
  onSubmit: (input: PartActionSubmitInput) => void | Promise<boolean | void>;
  submitting: boolean;
}

const PRIMARY_ACTIONS = [
  { code: PartActionType.INSTALL, label: 'Install' },
  { code: PartActionType.REPLACE, label: 'Replace' },
  { code: PartActionType.REMOVE, label: 'Remove' },
];

const OTHER_ACTIONS = [
  { code: PartActionType.DOA, label: 'DOA' },
  { code: PartActionType.CLEAN, label: 'Clean' },
  { code: PartActionType.TEST, label: 'Test' },
  { code: PartActionType.REPAIR, label: 'Repair' },
  { code: PartActionType.ADJUST, label: 'Adjust' },
];

const IN_PART_ACTIONS = new Set([
  PartActionType.INSTALL,
  PartActionType.REPLACE,
  PartActionType.DOA,
  PartActionType.CLEAN,
  PartActionType.TEST,
  PartActionType.REPAIR,
  PartActionType.ADJUST,
]);
const OUT_PART_ACTIONS = new Set([
  PartActionType.REMOVE,
  PartActionType.REPLACE,
  PartActionType.DOA,
]);

interface BuildPartDataOptions {
  qty?: number;
  source?: PartTypeLocationItemData | null;
}

const buildPartData = (
  part: PartTypeActionLiveSearchItem | null,
  opts: BuildPartDataOptions = {}
) => {
  if (!part) return {};
  const { qty = 1, source = null } = opts;
  return {
    partTypeId: part.partTypeId,
    name: part.partTypeName || part.name,
    serial: part.serial1 || '',
    partId: part.partId || 0,
    // `source.id` is the location-row id; the stock part id is `source.ns_partId`.
    // Using `source.id` here would make the BFF reference the wrong inventory record.
    ns_partId: source?.ns_partId ?? part.ns_partId ?? 0,
    category: part.categoryCode || '',
    condition: source?.conditionCode ?? part.conditionCode ?? '',
    location: source?.locationName ?? part.locationName ?? '',
    locationId: part.locationId || 0,
    qty,
    sw: part.softwareVersion || '',
    hw: part.hardwareVersion || '',
    fw: part.firmwareVersion || '',
    ft: source?.fleetTagName ?? part.fleetTagName ?? '',
  };
};

const buildExceptionalPartData = (data: ExceptionalPartData | null) => {
  if (!data || !data.partTypeId) return {};
  return {
    partTypeId: data.partTypeId,
    name: data.partTypeName,
    serial: data.serial,
    category: data.categoryCode,
    qty: 1,
    isExceptionalCreation: true,
  };
};

export const PartActionDialog: React.FC<PartActionDialogProps> = ({
  open,
  onClose,
  serviceId,
  clientId,
  contractId,
  locationId,
  serviceTypeCode,
  reportedSerialNumberPartId,
  canCreateExceptional,
  onSubmit,
  submitting,
}) => {
  const theme = useTheme();
  const [actionType, setActionType] = useState<PartActionType | ''>('');
  const [inPart, setInPart] = useState<PartTypeActionLiveSearchItem | null>(null);
  const [outPart, setOutPart] = useState<PartTypeActionLiveSearchItem | null>(null);
  const [notes, setNotes] = useState('');
  const [useExceptional, setUseExceptional] = useState(false);
  const [exceptionalData, setExceptionalData] = useState<ExceptionalPartData | null>(null);
  const [qty, setQty] = useState(1);
  const [source, setSource] = useState<PartTypeLocationItemData | null>(null);
  const [sourcePopupOpen, setSourcePopupOpen] = useState(false);
  // Bumped by resetForm to remount the whole DialogContent subtree, guaranteeing
  // the search fields' own internal state (selected serial, typed text) is
  // cleared on reset too — not just the parent state above.
  const [formResetKey, setFormResetKey] = useState(0);

  const isInConsumable =
    inPart?.categoryCode === PartTypeActionLiveSearchItemCategoryCode.Consumable;
  const isRepairService = serviceTypeCode === ServiceTypeCode.Repair;
  const partLocationsQuery = useGetPartLocationDataByServiceId(
    isInConsumable ? (inPart?.partTypeId ?? 0) : 0,
    serviceId
  );

  // Reset the form to its empty state. Called only after a confirmed-successful
  // submit (see handleSubmit) — never on a plain open or cancel — so a dialog
  // that was cancelled or accidentally dismissed keeps what the user entered.
  // The Dialog is `keepMounted` (below) so the child search fields also keep
  // their displayed selection across a close/re-open.
  const resetForm = useCallback(() => {
    setActionType('');
    setInPart(null);
    setOutPart(null);
    setNotes('');
    setUseExceptional(false);
    setExceptionalData(null);
    setQty(1);
    setSource(null);
    setSourcePopupOpen(false);
    setFormResetKey((key) => key + 1);
  }, []);

  // Close the nested source picker whenever the dialog closes so it can't
  // outlive its parent.
  useEffect(() => {
    if (!open) setSourcePopupOpen(false);
  }, [open]);

  // Clear qty/source when the selected in-part changes.
  useEffect(() => {
    setQty(1);
    setSource(null);
  }, [inPart?.partTypeId]);

  const needsIn = IN_PART_ACTIONS.has(actionType as PartActionType);
  const needsOut = OUT_PART_ACTIONS.has(actionType as PartActionType);

  // Mirrors the legacy ServiceActionBarFragment cards: surface a non-blocking
  // advisory when the selected IN part was found outside the expected hierarchy.
  // Purely informational — does not gate submit or alter the BFF payload.
  const unexpectedLocationWarning =
    needsIn && !useExceptional && inPart?.isInSiblingHierarchy === false && inPart.serial1
      ? partActionInValidations(
          inPart.conditionCode || '',
          inPart.serial1,
          inPart.locationName || ''
        )
      : '';

  const handleSubmit = async () => {
    // Only build/serialise sides the current action actually uses. `inPart` /
    // `outPart` / exceptional state can linger from earlier selections (e.g.
    // user picks INSTALL, selects an inPart, then switches to REMOVE); without
    // gating, the BFF receives stale installed/removed data for the wrong side.
    const isExceptional =
      needsIn && useExceptional && !!exceptionalData?.partTypeId && !!exceptionalData?.serial;
    const inData = !needsIn
      ? {}
      : isExceptional
        ? buildExceptionalPartData(exceptionalData)
        : buildPartData(inPart, isInConsumable ? { qty, source } : {});
    const outData = needsOut ? buildPartData(outPart) : {};
    const action: CreateBulkPartActionsRequest = {
      id: Date.now(),
      execute: true,
      actionTypeCode: actionType,
      serviceId,
      installedOrMaintainedPartData: inData as unknown as JSON,
      removedPartData: outData as unknown as JSON,
      notes,
      isExceptionalCreation: isExceptional,
    };

    const input: PartActionSubmitInput = { actions: [action] };
    if (isExceptional && exceptionalData) {
      input.exceptionalPart = {
        partTypeId: exceptionalData.partTypeId,
        serial1: exceptionalData.serial,
        conditionCode: 'good',
        serviceId,
        partActionType: actionType,
        imageData: [],
      };
    }
    // Reset only once the parent confirms the action committed (resolves true).
    // A failed submit or an interaction-form bounce resolves falsy, so the
    // entered details survive for the user to retry or amend.
    const committed = await onSubmit(input);
    if (committed) resetForm();
  };

  const hasInPart = !needsIn || !!inPart;
  const hasOutPart = !needsOut || !!outPart;
  const hasValidPart = useExceptional
    ? !!(exceptionalData?.serial && exceptionalData?.partTypeId) && hasOutPart
    : hasInPart && hasOutPart;
  // Only enforce qty/source when the current action actually needs an IN part —
  // a stale consumable inPart from a prior Install should not block a later REMOVE.
  const hasValidConsumable =
    !needsIn ||
    !isInConsumable ||
    (qty >= 1 && qty <= CONSUMABLE_MAX_QTY && (!isRepairService || !!source));
  const canSubmit = !!actionType && !submitting && hasValidPart && hasValidConsumable;

  const optionSx = (active: boolean) => ({
    flex: 1,
    px: 1.5,
    py: 1.5,
    borderRadius: 1,
    cursor: 'pointer',
    textAlign: 'center' as const,
    border: '1px solid',
    fontSize: '13px',
    fontWeight: 600,
    borderColor: active ? theme.palette.v4.accent : theme.palette.v4.border,
    bgcolor: active ? theme.palette.v4.accent : 'transparent',
    color: active ? theme.palette.common.white : theme.palette.text.primary,
    '&:hover': { borderColor: theme.palette.v4.accent, opacity: 0.85 },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth keepMounted>
      <DialogTitle
        sx={{ fontWeight: 600, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}
      >
        Part Action
        {canCreateExceptional && actionType && needsIn && (
          <Chip
            label={useExceptional ? 'Exceptional Creation' : 'Use Exceptional Creation'}
            size="small"
            onClick={() => setUseExceptional(!useExceptional)}
            sx={{
              ml: 'auto',
              height: 22,
              fontSize: '10px',
              fontWeight: 600,
              cursor: 'pointer',
              bgcolor: useExceptional ? 'warning.main' : 'v4.tableEven',
              color: useExceptional ? 'common.white' : 'text.secondary',
            }}
          />
        )}
      </DialogTitle>
      <DialogContent key={formResetKey}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box>
            <Typography
              sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', mb: 0.75 }}
            >
              Action Type
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
              {PRIMARY_ACTIONS.map((a) => (
                <Box
                  component="button"
                  type="button"
                  key={a.code}
                  aria-pressed={actionType === a.code}
                  sx={optionSx(actionType === a.code)}
                  onClick={() => setActionType(a.code)}
                >
                  {a.label}
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {OTHER_ACTIONS.map((a) => (
                <Box
                  component="button"
                  type="button"
                  key={a.code}
                  aria-pressed={actionType === a.code}
                  sx={optionSx(actionType === a.code)}
                  onClick={() => setActionType(a.code)}
                >
                  {a.label}
                </Box>
              ))}
            </Box>
          </Box>

          {needsOut && (
            <PartActionSearchField
              label={needsIn ? 'Part Out (Remove)' : 'Part to Remove'}
              clientId={clientId}
              contractId={contractId}
              locationId={locationId}
              serviceId={serviceId}
              actionType={actionType}
              action={Action.OUT}
              reportedSerialNumberPartId={reportedSerialNumberPartId}
              onSelect={setOutPart}
            />
          )}

          {needsIn && !useExceptional && (
            <PartActionSearchField
              label={needsOut ? 'Part In (Install)' : 'Part'}
              clientId={clientId}
              contractId={contractId}
              locationId={locationId}
              serviceId={serviceId}
              actionType={actionType}
              action={Action.IN}
              reportedSerialNumberPartId={reportedSerialNumberPartId}
              onSelect={setInPart}
            />
          )}

          {unexpectedLocationWarning && (
            <Alert severity="warning" sx={{ fontSize: '12px' }}>
              {unexpectedLocationWarning}
            </Alert>
          )}

          {needsIn && useExceptional && (
            <ExceptionalPartForm
              clientId={clientId}
              contractId={contractId}
              onDataChange={setExceptionalData}
            />
          )}

          {needsIn && !useExceptional && isInConsumable && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {isRepairService && (
                <Box>
                  <Typography
                    sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}
                  >
                    Source Location
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    onClick={() => setSourcePopupOpen(true)}
                    sx={{
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      fontSize: '12px',
                      color: 'v4.accent',
                      borderColor: 'v4.accent',
                      '&:hover': { borderColor: 'v4.accent', opacity: 0.85 },
                    }}
                  >
                    {source
                      ? `${source.locationName} — ${source.conditionCode} · QTY ${source.quantityInStock}`
                      : 'Select Location'}
                  </Button>
                </Box>
              )}
              <TextField
                size="small"
                type="number"
                label="Qty"
                value={qty}
                onChange={(e) => setQty(clampConsumableQty(e.target.value))}
                inputProps={{
                  min: 1,
                  max: CONSUMABLE_MAX_QTY,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
              />
            </Box>
          )}

          {actionType && (
            <TextField
              size="small"
              label="Notes"
              multiline
              minRows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            textTransform: 'none',
            bgcolor: 'v4.accent',
            '&:hover': { bgcolor: 'v4.accent', opacity: 0.9 },
          }}
        >
          {submitting ? <CircularProgress size={16} sx={{ color: 'common.white' }} /> : 'Submit'}
        </Button>
      </DialogActions>
      <PartLocationSelectPopup
        open={sourcePopupOpen}
        options={partLocationsQuery.data || []}
        onSelect={(picked) => {
          setSource(picked);
          // Keep qty in [1, stock] — a 0-stock source would otherwise drive qty
          // to 0 and block submission via hasValidConsumable.
          setQty((prev) =>
            Math.max(1, Math.min(prev, picked.quantityInStock ?? CONSUMABLE_MAX_QTY))
          );
          setSourcePopupOpen(false);
        }}
        onClose={() => setSourcePopupOpen(false)}
      />
    </Dialog>
  );
};
